import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle, Circle, Plus, Trash, PencilSimple, X, Check } from "@phosphor-icons/react";

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function OfficeRoutineCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [addForm, setAddForm] = useState({ label: "", days: [], link: "" });
  const [editItem, setEditItem] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/admin/routine").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const doneSet = new Set((data.done || []).map((d) => `${d.item_id}|${d.date}`));
  const fmtD = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" });
  const fmtLong = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const coversText = (it) => {
    if (it.days.length >= 7) {
      const y = new Date(data.today + "T12:00:00");
      y.setDate(y.getDate() - 1);
      const iso = y.toISOString().slice(0, 10);
      return `covers ${fmtD(iso)}, midnight to midnight`;
    }
    return `covers ${fmtD(data.week_dates[0])} – ${fmtD(data.week_dates[6])}`;
  };
  // exact report range to pull in HHAeXchange — never a partial day (owner rule 2026-09-07)
  const pullText = (it) => {
    if (it.days.length >= 7) {
      const y = new Date(data.today + "T12:00:00");
      y.setDate(y.getDate() - 1);
      return `Pull: ${fmtLong(y.toISOString().slice(0, 10))} — the full day, 12:00 AM to 11:59 PM`;
    }
    const t = new Date(data.today + "T12:00:00");
    const dow = t.getDay(); // Sun=0 … Sat=6
    const lastSat = new Date(t);
    lastSat.setDate(t.getDate() - ((dow + 1) % 7)); // most recent Saturday strictly before today (today's week is incomplete)
    const sun = new Date(lastSat);
    sun.setDate(lastSat.getDate() - 6);
    return `Pull: ${fmtLong(sun.toISOString().slice(0, 10))} – ${fmtLong(lastSat.toISOString().slice(0, 10))} (last complete Sun–Sat week)`;
  };
  const visitedPaths = data.visited || [];
  const linkVisited = (it, date) => it.link && visitedPaths.some((v) => v.date === date && (v.path === it.link || v.path.startsWith(it.link + "/")));
  const visitBadge = (it, date) => {
    if (!it.link) return null;
    return linkVisited(it, date)
      ? <span className="rounded-full border border-[#27AE60]/40 bg-[#27AE60]/10 px-2 py-0.5 text-[10px] font-bold text-[#1e8449]" data-testid={`routine-visited-${it.id}`}>page visited</span>
      : <span className="rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]" data-testid={`routine-not-visited-${it.id}`}>page not visited</span>;
  };
  const todayDow = new Date(data.today + "T12:00:00").getDay();
  const todayIdx = (todayDow + 6) % 7; // JS Sun=0 → Mon=0
  const todays = data.items.filter((it) => it.days.includes(todayIdx));
  const dutyImport = data.duty_import || null;
  const dutyRange = dutyImport?.latest?.date_from
    ? `${fmtD(dutyImport.latest.date_from)} – ${fmtD(dutyImport.latest.date_to)}`
    : "no import range recorded";

  const toggle = async (itemId, date) => {
    try {
      await api.post(`/admin/routine/items/${itemId}/toggle`, { date });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addItem = async () => {
    try {
      await api.post("/admin/routine/items", addForm);
      setAddForm({ label: "", days: [], link: "" });
      toast.success("Routine item added");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/routine/items/${editItem.id}`, { label: editItem.label, days: editItem.days, link: editItem.link });
      setEditItem(null);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeItem = async (it) => {
    if (!window.confirm(`Remove "${it.label}" from the routine?`)) return;
    try { await api.delete(`/admin/routine/items/${it.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const dayPills = (days, onToggle, tidPrefix) => (
    <span className="inline-flex gap-1">
      {DAY_LETTERS.map((L, i) => (
        <button key={i} type="button" onClick={() => onToggle(i)}
          className={`h-6 w-6 rounded-full border text-[10px] font-bold transition-colors ${days.includes(i) ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-400 hover:border-stone-400"}`}
          data-testid={`${tidPrefix}-day-${i}`}>{L}</button>
      ))}
    </span>
  );

  return (
    <div className="mb-8 rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="office-routine-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-100 px-6 py-4">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-[#4A235A]">Office routine</h2>
          <p className="text-xs text-stone-500">The standing schedule — what the office does every day and every week.</p>
        </div>
        <button onClick={() => setEditing((v) => !v)} className="text-xs font-semibold text-stone-400 hover:text-stone-700 hover:underline" data-testid="routine-edit-toggle">
          {editing ? "Done editing" : "Edit routine"}
        </button>
      </div>

      <div className="px-6 py-4">
        {dutyImport && (
          <div className={`mb-4 rounded-lg border px-4 py-3 ${dutyImport.covered ? "border-emerald-200 bg-emerald-50" : "border-[#B7791F]/30 bg-[#B7791F]/10"}`}
            data-testid={dutyImport.covered ? "duty-import-current" : "duty-import-warning"}>
            <p className={`text-xs font-bold ${dutyImport.covered ? "text-emerald-700" : "text-[#8a5d14]"}`}>
              {dutyImport.covered ? "Duty import is current" : "Yesterday's duty upload needs attention"}
            </p>
            <p className={`mt-0.5 text-[11px] ${dutyImport.covered ? "text-emerald-700" : "text-[#8a5d14]"}`} data-testid="duty-import-range">
              Required: {fmtLong(dutyImport.required_date)} · Latest recorded range: {dutyRange}
            </p>
            <p className={`mt-0.5 text-[11px] ${dutyImport.covered ? "text-emerald-700" : "text-[#8a5d14]"}`} data-testid="duty-import-last-upload">
              Last visit upload: {dutyImport.latest?.imported_at ? `${new Date(dutyImport.latest.imported_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}${dutyImport.latest?.filename ? ` · ${dutyImport.latest.filename}` : ""}` : "never"}
            </p>
          </div>
        )}
        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">Today — {DAY_NAMES[todayIdx]} {data.today}</p>
        {todays.length === 0 ? (
          <p className="mt-2 text-sm text-stone-400" data-testid="routine-today-empty">Nothing scheduled for today.</p>
        ) : (
          <div className="mt-2 space-y-1.5" data-testid="routine-today-list">
            {todays.map((it) => {
              const done = doneSet.has(`${it.id}|${data.today}`);
              return (
                <div key={it.id} className="flex items-center gap-3 rounded-lg border border-stone-100 px-3 py-2" data-testid={`routine-item-${it.id}`}>
                  <button onClick={() => toggle(it.id, data.today)} data-testid={`routine-toggle-${it.id}`}>
                    {done ? <CheckCircle size={22} weight="fill" className="text-[#27AE60]" /> : <Circle size={22} className="text-stone-300 hover:text-stone-500" />}
                  </button>
                  <button onClick={() => it.link && navigate(it.link)}
                    className={`flex-1 text-left text-sm ${done ? "text-stone-400 line-through" : "font-medium text-stone-800"} ${it.link ? "hover:text-primary hover:underline" : ""}`}
                    data-testid={`routine-link-${it.id}`}>
                    {it.label}
                    <span className={`block text-[10px] font-semibold ${done ? "text-stone-300" : "text-primary/80"}`} data-testid={`routine-pull-${it.id}`}>{pullText(it)}</span>
                    <span className={`block text-[10px] font-normal ${done ? "text-stone-300" : "text-stone-400"}`} data-testid={`routine-covers-${it.id}`}>{coversText(it)}</span>
                  </button>
                  {!done && visitBadge(it, data.today)}
                  {done && <span className="text-[10px] text-stone-400">{(data.done.find((d) => d.item_id === it.id && d.date === data.today) || {}).by}</span>}
                  {editing && (
                    <span className="flex items-center gap-1">
                      <button onClick={() => setEditItem({ ...it })} className="text-stone-400 hover:text-primary" data-testid={`routine-edit-${it.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => removeItem(it)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`routine-del-${it.id}`}><Trash size={14} /></button>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 overflow-x-auto">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-400">This week</p>
          <div className="mt-2 grid min-w-[560px] grid-cols-7 gap-1.5" data-testid="routine-week">
            {data.week_dates.map((date, di) => {
              const dayItems = data.items.filter((it) => it.days.includes(di));
              const isToday = date === data.today;
              return (
                <div key={date} className={`rounded-lg border p-2 ${isToday ? "border-primary/40 bg-primary/[0.04]" : "border-stone-100"}`} data-testid={`routine-day-${di}`}>
                  <p className={`text-[10px] font-bold uppercase ${isToday ? "text-primary" : "text-stone-400"}`}>{DAY_NAMES[di]} <span className="font-normal">{date.slice(5)}</span></p>
                  <div className="mt-1 space-y-1">
                    {dayItems.map((it) => {
                      const done = doneSet.has(`${it.id}|${date}`);
                      const past = date <= data.today;
                      const vdot = it.link && past && !done
                        ? (linkVisited(it, date) ? <span className="mr-0.5 text-[#27AE60]">●</span> : <span className="mr-0.5 text-[#C0392B]">●</span>)
                        : null;
                      return (
                        <button key={it.id} onClick={() => toggle(it.id, date)} title={`${it.label} — ${pullText(it)}${it.link && past ? (linkVisited(it, date) ? " · page visited" : " · page NOT visited") : ""}`}
                          className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] transition-colors ${done ? "bg-[#27AE60]/10 text-[#1e8449] line-through" : "bg-stone-50 text-stone-600 hover:bg-stone-100"}`}
                          data-testid={`routine-week-${it.id}-${date}`}>
                          {vdot}{done ? "✓ " : ""}{it.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {editing && (
          <div className="mt-4 rounded-lg border border-primary/25 bg-primary/[0.03] p-4" data-testid="routine-add-form">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">Add a routine item</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                placeholder="e.g. Upload duty codes — yesterday, midnight to midnight"
                className="h-8 min-w-[260px] flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="routine-add-label" />
              {dayPills(addForm.days, (i) => setAddForm((f) => ({ ...f, days: f.days.includes(i) ? f.days.filter((d) => d !== i) : [...f.days, i] })), "routine-add")}
              <input value={addForm.link} onChange={(e) => setAddForm({ ...addForm, link: e.target.value })}
                placeholder="Link (optional, e.g. /clients)"
                className="h-8 w-44 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="routine-add-link" />
              <button onClick={addItem} disabled={!addForm.label.trim() || !addForm.days.length}
                className="inline-flex h-8 items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid="routine-add-submit">
                <Plus size={13} className="mr-1" /> Add
              </button>
            </div>
          </div>
        )}

        {editItem && (
          <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4" data-testid="routine-edit-form">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Edit item</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input value={editItem.label} onChange={(e) => setEditItem({ ...editItem, label: e.target.value })}
                className="h-8 min-w-[260px] flex-1 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="routine-edit-label" />
              {dayPills(editItem.days, (i) => setEditItem((f) => ({ ...f, days: f.days.includes(i) ? f.days.filter((d) => d !== i) : [...f.days, i] })), "routine-edit")}
              <input value={editItem.link} onChange={(e) => setEditItem({ ...editItem, link: e.target.value })}
                className="h-8 w-44 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="routine-edit-link" />
              <button onClick={saveEdit} className="inline-flex h-8 items-center rounded-full bg-primary px-3 text-xs font-semibold text-primary-foreground" data-testid="routine-edit-save"><Check size={13} className="mr-1" /> Save</button>
              <button onClick={() => setEditItem(null)} className="text-stone-400" data-testid="routine-edit-cancel"><X size={15} /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
